import { create } from 'zustand';
import { ListEvents, CreateEvent, GetEvent, UpdateEvent, DeleteEvent } from '../api/api';
import toast from 'react-hot-toast';

export interface Event {
  _id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  type: 'service' | 'special' | 'community';
  image?: string;
  description?: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreateEventI {
  title: string;
  date: string;
  time: string;
  location: string;
  type: 'service' | 'special' | 'community';
  image?: string;
  description?: string;
}

interface UpdateEventI {
  title?: string;
  date?: string;
  time?: string;
  location?: string;
  type?: 'service' | 'special' | 'community';
  image?: string;
  description?: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface GetEventsResponse {
  data: {
    events: Event[];
    pagination?: PaginationInfo;
  };
}

interface EventResponse {
  data: {
    event: Event;
  };
  status: boolean;
  message: string;
}

interface DeleteEventResponse {
  status: boolean;
  message: string;
}

interface EventStore {
  events: Event[];
  loading: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  getEvents: (isPublic?: boolean, search?: string, page?: number, limit?: number) => Promise<void>;
  createEvent: (event: CreateEventI) => Promise<void>;
  getEventById: (id: string) => Promise<Event | null>;
  updateEvent: (id: string, event: UpdateEventI) => Promise<void>;
  deleteEvent: (id: string) => Promise<void>;
}

export const useEventStore = create<EventStore>((set, get) => ({
  events: [] as Event[],
  loading: false,
  error: null,
  pagination: null,

  getEvents: async (isPublic?: boolean, search?: string, page?: number, limit?: number) => {
    set({ loading: true, error: null });
    const { data, error } = await ListEvents<GetEventsResponse>(isPublic, search, page, limit);

    if (error) {
      set({ loading: false, error: error as string });
      return;
    }

    set({
      events: data.data.events || [],
      pagination: data.data.pagination || null,
      loading: false,
    });
  },

  createEvent: async (event: CreateEventI) => {
    set({ loading: true, error: null });
    const { data, error } = await CreateEvent<EventResponse>(event);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to create event' });
      return;
    }

    const events = get().events;
    set({ events: [data.data.event, ...events], loading: false });
    toast.success('Event created successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getEvents(undefined, undefined, currentPagination?.page, currentPagination?.limit);
  },

  getEventById: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await GetEvent<EventResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to get event' });
      return null;
    }

    set({ loading: false });
    return data.data.event;
  },

  updateEvent: async (id: string, event: UpdateEventI) => {
    set({ loading: true, error: null });
    const { data, error } = await UpdateEvent<EventResponse>(id, event);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to update event' });
      return;
    }

    const events = get().events;
    const updatedEvents = events.map((e) => (e._id === id ? { ...e, ...data.data.event } : e));
    set({ events: updatedEvents, loading: false });
    toast.success('Event updated successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getEvents(undefined, undefined, currentPagination?.page, currentPagination?.limit);
  },

  deleteEvent: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await DeleteEvent<DeleteEventResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to delete event' });
      return;
    }

    const events = get().events.filter((event) => event._id !== id);
    set({ events, loading: false });
    toast.success('Event deleted successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getEvents(undefined, undefined, currentPagination?.page, currentPagination?.limit);
  },
}));

export type { CreateEventI, UpdateEventI };
